import React from "react";
import { useI18n } from "@/i18n/I18nContext";
import NewsletterForm from "@/components/NewsletterForm";
import consultant from "@/assets/consultant.jpg";
import meeting from "@/assets/meeting.jpg";
import office from "@/assets/office.jpg";
import education from "@/assets/education.jpg";
import retirement from "@/assets/retirement.jpg";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-16 sm:py-20 lg:py-24 ${className}`}>
    <div className="mx-auto max-w-6xl px-5 lg:px-8">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <span className="inline-block rounded-full border border-[#0D7A5F]/30 bg-[#0D7A5F]/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[#0D7A5F]">
    {children}
  </span>
);

const Home = () => {
  const { t } = useI18n();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-5 py-16 sm:py-20 lg:px-8 lg:py-24">
          <Eyebrow>{t("home.hero.eyebrow")}</Eyebrow>
          <h1 className="mt-5 font-serif text-3xl font-bold tracking-tight text-[#0F2C59] sm:text-4xl lg:text-5xl">
            {t("home.hero.title")}
          </h1>
          <p className="mt-5 max-w-2xl text-base text-[#475569] leading-relaxed">
            {t("home.hero.subtitle")}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href="#newsletter"
              className="inline-flex items-center justify-center rounded-md bg-[#0D7A5F] px-5 py-3 text-sm font-semibold text-white hover:bg-[#0B6B53]"
              data-testid="hero-cta-primary"
            >
              {t("home.hero.ctaPrimary")}
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-center rounded-md border border-[#0F2C59] px-5 py-3 text-sm font-semibold text-[#0F2C59] hover:bg-[#0F2C59]/5"
              data-testid="hero-cta-secondary"
            >
              {t("home.hero.ctaSecondary")}
            </a>
          </div>
          <p className="mt-6 text-xs uppercase tracking-widest text-[#0F2C59]/70">
            {t("home.hero.trustBadge")}
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <div className="border-y border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 text-sm text-[#0F2C59] sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
          <div className="border-l-2 border-[#0D7A5F] pl-3">{t("home.trustBar.item1")}</div>
          <div className="border-l-2 border-[#0D7A5F] pl-3">{t("home.trustBar.item2")}</div>
          <div className="border-l-2 border-[#0D7A5F] pl-3">{t("home.trustBar.item3")}</div>
          <div className="border-l-2 border-[#0D7A5F] pl-3">{t("home.trustBar.item4")}</div>
        </div>
      </div>

      {/* Eligibility */}
      <Section id="eligibility" className="bg-white">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow>{t("home.eligibility.eyebrow")}</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-semibold text-[#0F2C59] sm:text-3xl lg:text-4xl">
              {t("home.eligibility.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#475569]">
              {t("home.eligibility.description")}
            </p>
            <a
              href="#newsletter"
              className="mt-6 inline-flex items-center justify-center rounded-md bg-[#0F2C59] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1B365D]"
              data-testid="eligibility-cta"
            >
              {t("home.eligibility.cta")}
            </a>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-4">
              {["one", "two", "three"].map((key, idx) => (
                <div
                  key={key}
                  className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-5"
                  data-testid={`eligibility-point-${idx + 1}`}
                >
                  <p className="font-serif text-lg font-semibold text-[#0F2C59]">
                    {t(`home.eligibility.points.${key}.title`)}
                  </p>
                  <p className="mt-2 text-sm text-[#475569]">
                    {t(`home.eligibility.points.${key}.text`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Services */}
      <Section id="services" className="bg-[#F8FAFC]">
        <div className="max-w-3xl">
          <Eyebrow>{t("home.services.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl font-semibold text-[#0F2C59] sm:text-3xl lg:text-4xl">
            {t("home.services.title")}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#475569]">
            {t("home.services.description")}
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {[
            { key: "one", img: consultant, alt: "Consultor financiero" },
            { key: "two", img: education, alt: "Educación financiera" },
            { key: "three", img: meeting, alt: "Asesoría de deuda" },
            { key: "four", img: retirement, alt: "Planeación patrimonial" },
          ].map((item, idx) => (
            <article
              key={item.key}
              className="overflow-hidden rounded-md border border-[#E2E8F0] bg-white"
              data-testid={`service-card-${idx + 1}`}
            >
              <div className="h-48 w-full overflow-hidden">
                <img src={item.img} alt={item.alt} className="h-full w-full object-cover" />
              </div>
              <div className="p-6">
                <p className="font-serif text-lg font-semibold text-[#0F2C59]">
                  {t(`home.services.cards.${item.key}.title`)}
                </p>
                <p className="mt-2 text-sm text-[#475569]">
                  {t(`home.services.cards.${item.key}.text`)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* Philosophy */}
      <Section id="about" className="bg-white">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-md border border-[#E2E8F0]">
              <img src={office} alt="Oficina Kredamontiva CDMX" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Eyebrow>{t("home.philosophy.eyebrow")}</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-semibold text-[#0F2C59] sm:text-3xl lg:text-4xl">
              {t("home.philosophy.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#475569]">
              {t("home.philosophy.paragraph1")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#475569]">
              {t("home.philosophy.paragraph2")}
            </p>
            <blockquote className="mt-6 border-l-4 border-[#D4AF37] bg-[#F8FAFC] p-5">
              <p className="font-serif text-lg italic text-[#0F2C59]">
                {t("home.philosophy.quote")}
              </p>
              <footer className="mt-3 text-xs uppercase tracking-widest text-[#475569]">
                {t("home.philosophy.quoteAuthor")}
              </footer>
            </blockquote>
          </div>
        </div>
      </Section>

      {/* Process */}
      <Section id="resources" className="bg-[#0F2C59] text-white">
        <div className="max-w-3xl">
          <span className="inline-block rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[#D4AF37]">
            {t("home.process.eyebrow")}
          </span>
          <h2 className="mt-4 font-serif text-2xl font-semibold sm:text-3xl lg:text-4xl">
            {t("home.process.title")}
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {["one", "two", "three", "four"].map((key, idx) => (
            <div
              key={key}
              className="rounded-md border border-white/15 bg-[#0b224a] p-5"
              data-testid={`process-step-${idx + 1}`}
            >
              <p className="font-serif text-lg font-semibold text-white">
                {t(`home.process.steps.${key}.title`)}
              </p>
              <p className="mt-2 text-sm text-white/80">
                {t(`home.process.steps.${key}.text`)}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-white">
        <div className="max-w-3xl">
          <Eyebrow>{t("home.faq.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl font-semibold text-[#0F2C59] sm:text-3xl lg:text-4xl">
            {t("home.faq.title")}
          </h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {["one", "two", "three", "four"].map((key, idx) => (
            <div
              key={key}
              className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-5"
              data-testid={`faq-item-${idx + 1}`}
            >
              <p className="font-serif text-lg font-semibold text-[#0F2C59]">
                {t(`home.faq.items.${key}.q`)}
              </p>
              <p className="mt-2 text-sm text-[#475569]">
                {t(`home.faq.items.${key}.a`)}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Newsletter CTA */}
      <Section id="newsletter" className="bg-[#F8FAFC]">
        <div className="grid gap-10 rounded-lg border border-[#E2E8F0] bg-white p-8 lg:grid-cols-12 lg:p-12">
          <div className="lg:col-span-5">
            <Eyebrow>{t("home.newsletter.eyebrow")}</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-semibold text-[#0F2C59] sm:text-3xl lg:text-4xl">
              {t("home.newsletter.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#475569]">
              {t("home.newsletter.description")}
            </p>
          </div>
          <div className="lg:col-span-7">
            <NewsletterForm />
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
